/**
 * Represents domain properties for notification display.
 */
export interface NotificationContentSnapshot {
  type: string;
  createdAt: number; // timestamp in milliseconds
  clientName: string | null;
  title: string | null;
  status: string | null;
  priority: string | null;
  subTitle: string | null;
  updatedAt: number | null; // timestamp in milliseconds
  createdByName: string | null;
  resolveByName: string | null;
  summary: string | null;
  context: string | null;
  projectName: string | null;
  subProjectName: string | null;
  score: number | null;
}

export type Actions = 'reply' | 'goToChat' | 'archive' | 'complete' | 'comment';

export type NotificationStatus = 'active' | 'archived' | 'completed';

/**
 * Index fields for efficient Firebase queries.
 * These fields are populated by the backend and used for filtering/pagination.
 */
export interface NotificationIndexFields {
  createdAt_index: number;
  status_createdAt_index: string;
  sourceType_createdAt_index: string;
  sourceType_status_createdAt_index: string;
}

/**
 * Complete notification record structure.
 * Firebase always receives the complete record (snapshot, not patches).
 */
export interface NotificationRecord extends NotificationIndexFields {
  // notification properties
  sourceType: string;
  sourceId: number;
  idempotencyKey: string;
  // When the record was first persisted for this user. Legacy records may not have it.
  deliveredAt?: number;
  // States of notification, affect user view
  status: NotificationStatus;
  // Per-notification read state: absent/null when unread, epoch ms when read
  readAt?: number | null;
  // Domains properties
  projectId: number | null;
  subProjectId: number | null;
  clientId: number | null;
  messageId: number | null;
  contentSnapshot: NotificationContentSnapshot;
  actions: Actions[];
}

export interface NotificationCursor {
  key: string;
  value: number | string;
}

export interface NotificationCenterViewState {
  datePreset: 'all' | 'today' | 'last7' | 'last30';
  lastOpenedNotificationKey: string | null;
  search: string;
  scrollTop: number;
}

/**
 * Cache entry for a specific filter combination (domain + status).
 */
export interface FilterCacheEntry {
  items: Record<string, NotificationRecord>;
  cursor: NotificationCursor | null;
  hasMore: boolean;
  isLoading: boolean;
}

/**
 * Default filter key used on initial load.
 */
export const DEFAULT_FILTER_KEY = 'all_active';

export interface NotificationsState {
  filterCache: Record<string, FilterCacheEntry>; // key = "{domain}_{status}"
  activeFilterKey: string; // current filter combination
  unseenCount: number; // notifications created since the center was last opened
  readAllBefore: number | null; // global read watermark, independent from unseenCount
  isCenterOpen: boolean; // if the notification center drawer is open
  viewState: NotificationCenterViewState;
}
